import type { FC, ReactNode } from 'react'
import type { Cart, MenuCategory, MenuItem } from '@/types'

import { Check, Phone, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/format'
import { buttonClasses } from '@/components/generals/button'
import { SectionHeading } from '@/components/landing/section-heading'
import { freeToppings, siteConfig } from '@/constants/site'

interface MenuSectionProps {
    categories: Array<MenuCategory>
    cart: Cart
    onAdd: (itemId: string) => void
}

interface MenuItemProps {
    item: MenuItem
    quantity: number
    onAdd: (itemId: string) => void
}

const AddButton: FC<MenuItemProps & { compact?: boolean }> = ({ item, quantity, onAdd, compact = false }): ReactNode => {
    if (!item.isAvailable) {
        return (
            <span className='inline-flex h-9 items-center rounded-full bg-muted px-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                Sold out today
            </span>
        )
    }

    return (
        <button
            type='button'
            onClick={() => onAdd(item.id)}
            aria-label={`Add ${item.name} to your order`}
            className={buttonClasses(quantity > 0 ? 'primary' : 'secondary', compact ? 'icon-sm' : 'sm')}
        >
            {quantity > 0 ? <Check aria-hidden='true' /> : <Plus aria-hidden='true' />}

            {!compact && (
                <span>
                    {quantity > 0 ? `Added (${quantity})` : "Add to order"}
                </span>
            )}
        </button>
    )
}

const FeaturedItem: FC<MenuItemProps> = ({ item, quantity, onAdd }): ReactNode => {
    return (
        <li
            className={cn(
                'flex gap-5 rounded-[1.75rem] border bg-background p-5 transition-[border-color,box-shadow] duration-300 sm:p-6',
                quantity > 0 ? 'border-primary/40 shadow-lg shadow-primary/10' : 'border-border hover:border-primary/25 hover:shadow-lg hover:shadow-broth/5',
                !item.isAvailable && 'opacity-75'
            )}
        >
            {item.image && (
                <img
                    src={item.image}
                    alt={item.imageAlt || item.name}
                    width={112}
                    height={112}
                    loading='lazy'
                    decoding='async'
                    className='size-24 shrink-0 rounded-2xl bg-muted object-cover sm:size-28'
                />
            )}

            <div className='flex min-w-0 flex-1 flex-col'>
                {item.badge && (
                    <span
                        data-mdlfy-cms={`menu_items:${item.id}:badge`}
                        className='mb-2 w-fit rounded-full bg-scallion-soft px-2.5 py-0.5 text-xs font-semibold text-scallion'
                    >
                        {item.badge}
                    </span>
                )}

                <div className='flex items-start justify-between gap-4'>
                    <h4
                        data-mdlfy-cms={`menu_items:${item.id}:name`}
                        className='font-heading text-xl font-semibold leading-snug'
                    >
                        {item.name}
                    </h4>

                    <p className='shrink-0 font-heading text-xl font-semibold tabular-nums text-primary'>
                        {formatPrice(item.price)}
                    </p>
                </div>

                {item.description && (
                    <p
                        data-mdlfy-cms={`menu_items:${item.id}:description`}
                        className='mt-2 text-sm leading-relaxed text-muted-foreground'
                    >
                        {item.description}
                    </p>
                )}

                <div className='mt-auto pt-5'>
                    <AddButton item={item} quantity={quantity} onAdd={onAdd} />
                </div>
            </div>
        </li>
    )
}

const CompactItem: FC<MenuItemProps> = ({ item, quantity, onAdd }): ReactNode => {
    return (
        <li className={cn('flex items-center gap-4 py-4', !item.isAvailable && 'opacity-75')}>
            {item.image && (
                <img
                    src={item.image}
                    alt={item.imageAlt || item.name}
                    width={56}
                    height={56}
                    loading='lazy'
                    decoding='async'
                    className='size-14 shrink-0 rounded-xl bg-muted object-cover'
                />
            )}

            <div className='min-w-0 flex-1'>
                <div className='flex items-baseline gap-3'>
                    <h4
                        data-mdlfy-cms={`menu_items:${item.id}:name`}
                        className='font-semibold'
                    >
                        {item.name}
                    </h4>

                    <span aria-hidden='true' className='min-w-4 flex-1 -translate-y-1 border-b-2 border-dotted border-foreground/15' />

                    <p className='shrink-0 font-semibold tabular-nums'>
                        {formatPrice(item.price)}
                    </p>
                </div>

                {item.description && (
                    <p
                        data-mdlfy-cms={`menu_items:${item.id}:description`}
                        className='mt-1 text-sm leading-relaxed text-muted-foreground'
                    >
                        {item.description}
                    </p>
                )}

                {item.badge && (
                    <span
                        data-mdlfy-cms={`menu_items:${item.id}:badge`}
                        className='mt-2 inline-block rounded-full bg-scallion-soft px-2.5 py-0.5 text-xs font-semibold text-scallion'
                    >
                        {item.badge}
                    </span>
                )}
            </div>

            <AddButton item={item} quantity={quantity} onAdd={onAdd} compact />
        </li>
    )
}

const CategoryHeading: FC<{ category: MenuCategory, large?: boolean }> = ({ category, large = false }): ReactNode => {
    return (
        <div className='border-b border-border pb-4'>
            <h3
                data-mdlfy-cms={`menu_categories:${category.id}:name`}
                className={cn('font-semibold tracking-tight', large ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl')}
            >
                {category.name}
            </h3>

            {category.description && (
                <p
                    data-mdlfy-cms={`menu_categories:${category.id}:description`}
                    className='mt-2 text-muted-foreground'
                >
                    {category.description}
                </p>
            )}
        </div>
    )
}

export const MenuSection: FC<MenuSectionProps> = ({ categories, cart, onAdd }): ReactNode => {
    const [featured, ...others] = categories

    return (
        <section id='menu' aria-labelledby='menu-title' className='scroll-mt-20 border-y border-border bg-card py-20 sm:py-28'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        titleId='menu-title'
                        eyebrow="The menu"
                        title={<>Pick your bowl, <em>we&apos;ll do the rest.</em></>}
                        description="Every bowl starts with the same silky base. Add it to your order here and pick it up hot."
                    />

                    <div className='rounded-[1.5rem] border border-border bg-background p-5 lg:max-w-sm'>
                        <p className='text-xs font-semibold uppercase tracking-[0.18em] text-primary'>
                            Free on the side, always
                        </p>

                        <ul className='mt-3 flex flex-wrap gap-1.5'>
                            {freeToppings.map(topping => (
                                <li key={topping} className='rounded-full border border-border bg-card px-3 py-1 text-sm'>
                                    {topping}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {!featured ? (
                    <div className='mt-14 rounded-[1.75rem] border border-dashed border-border bg-background p-10 text-center'>
                        <h3 className='text-2xl font-semibold'>
                            Today&apos;s menu is being written up.
                        </h3>

                        <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                            Give us a call and we will tell you what is in the pot this morning.
                        </p>

                        <a href={siteConfig.phoneHref} className={buttonClasses('primary', 'md', 'mt-6')}>
                            <Phone />
                            Call {siteConfig.phone}
                        </a>
                    </div>
                ) : (
                    <>
                        <div className='mt-16'>
                            <CategoryHeading category={featured} large />

                            <ul className='mt-8 grid gap-5 md:grid-cols-2'>
                                {featured.items.map(item => (
                                    <FeaturedItem
                                        key={item.id}
                                        item={item}
                                        quantity={cart[item.id] ?? 0}
                                        onAdd={onAdd}
                                    />
                                ))}
                            </ul>
                        </div>

                        {others.length > 0 && (
                            <div className='mt-16 grid gap-x-16 gap-y-14 md:grid-cols-2'>
                                {others.map(category => (
                                    <div key={category.id}>
                                        <CategoryHeading category={category} />

                                        <ul className='divide-y divide-border/70'>
                                            {category.items.map(item => (
                                                <CompactItem
                                                    key={item.id}
                                                    item={item}
                                                    quantity={cart[item.id] ?? 0}
                                                    onAdd={onAdd}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    )
}
